import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import Breadcrumbs from "@/components/Breadcrumbs";
import Pagination, { usePaginatedSlice } from "@/components/Pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const REGIONS = ["Toutes", "Provence-Alpes-Côte d'Azur", "Pays de la Loire", "Bretagne", "Auvergne-Rhône-Alpes", "Occitanie", "Île-de-France", "Nouvelle-Aquitaine"];

import { Bell } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

function AlertButton() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(""); const [pt, setPt] = useState("");
  const [region, setR] = useState(""); const [mb, setMb] = useState(""); const [mm, setMm] = useState("");
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/alerts", { email, property_type: pt || null, region: region || null, max_bouquet: mb ? Number(mb) : null, max_monthly: mm ? Number(mm) : null });
      toast.success("Alerte créée. Vous serez notifié dès qu'un bien correspond."); setOpen(false);
    } catch { toast.error("Erreur"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="alert-btn" className="ml-auto inline-flex items-center gap-2 border border-navy/20 px-4 py-2 text-sm font-bold text-navy hover:bg-navy hover:text-cream transition-colors">
          <Bell size={15} /> Créer une alerte
        </button>
      </DialogTrigger>
      <DialogContent><DialogHeader><DialogTitle>Recevoir les nouveaux biens par email</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3" data-testid="alert-form">
          <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Votre email" />
          <Input value={pt} onChange={(e) => setPt(e.target.value)} placeholder="Type (libre / occupee, optionnel)" />
          <Input value={region} onChange={(e) => setR(e.target.value)} placeholder="Région (optionnel)" />
          <Input type="number" value={mb} onChange={(e) => setMb(e.target.value)} placeholder="Bouquet max € (optionnel)" />
          <Input type="number" value={mm} onChange={(e) => setMm(e.target.value)} placeholder="Mensualité max € (optionnel)" />
          <button className="w-full bg-accent-brand text-white py-3 font-bold hover:bg-[#B08D22]" data-testid="alert-submit">Activer l'alerte</button>
          <p className="text-xs text-navy/50">Vous serez notifié par email dès qu'un bien correspondant est publié.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Annonces() {
  const [all, setAll] = useState([]);
  const [type, setType] = useState("Tous");
  const [region, setRegion] = useState("Toutes");
  const [maxBouquet, setMaxBouquet] = useState("");
  const [maxMonthly, setMaxMonthly] = useState("");
  const [, setParams] = useSearchParams();

  useEffect(() => {
    api.get("/listings?limit=1000").then(({ data }) => setAll(data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return all.filter((l) => {
      if (type !== "Tous" && l.property_type !== type) return false;
      if (region !== "Toutes" && l.region !== region) return false;
      if (maxBouquet && l.bouquet > Number(maxBouquet)) return false;
      if (maxMonthly && l.monthly_payment > Number(maxMonthly)) return false;
      return true;
    });
  }, [all, type, region, maxBouquet, maxMonthly]);

  // Réinitialise la page dès qu'un filtre change (évite d'atterrir sur une page vide).
  const resetPage = () => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete("page");
      return p;
    });
  };

  const { page, totalPages, start, end } = usePaginatedSlice(filtered.length, 12);
  const pageItems = filtered.slice(start, end);

  return (
    <>
      <SEO title="Annonces de vente à terme — venteaterme.fr" description="Découvrez nos biens en vente à terme : maisons, appartements, libre ou occupée, partout en France. Filtres par région, budget et mensualité." />
      <section className="pt-24 pb-12 bg-navy text-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="text-xs uppercase tracking-[0.3em] text-accent-brand font-bold mb-4">Nos biens</div>
          <h1 className="font-serif text-4xl md:text-6xl leading-tight">Annonces en vente à terme</h1>
          <p className="text-cream/80 mt-6 max-w-2xl">Une sélection de biens à travers la France — libre ou occupée.</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          {/* Filters */}
          <div className="bg-white border border-navy/10 p-6 md:p-8 mb-12 grid md:grid-cols-4 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-navy/60 font-bold block mb-2">Type</label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="filter-type" className="bg-white border-navy/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Tous">Tous</SelectItem>
                  <SelectItem value="libre">Libre</SelectItem>
                  <SelectItem value="occupee">Occupée</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-navy/60 font-bold block mb-2">Région</label>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger data-testid="filter-region" className="bg-white border-navy/20"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-navy/60 font-bold block mb-2">Bouquet max (€)</label>
              <Input data-testid="filter-max-bouquet" type="number" value={maxBouquet} onChange={(e) => setMaxBouquet(e.target.value)} placeholder="ex: 300000" className="bg-white border-navy/20" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-navy/60 font-bold block mb-2">Mensualité max (€)</label>
              <Input data-testid="filter-max-monthly" type="number" value={maxMonthly} onChange={(e) => setMaxMonthly(e.target.value)} placeholder="ex: 2500" className="bg-white border-navy/20" />
            </div>
          </div>

          <div className="text-sm text-navy/60 mb-6 flex items-center" data-testid="listings-count">
            {filtered.length} annonce{filtered.length > 1 ? "s" : ""}
            {totalPages > 1 && (
              <span className="ml-2 text-navy/40 tabular-nums">— page {page}/{totalPages}</span>
            )}
            <AlertButton />
          </div>

          {filtered.length === 0 ? (
            <div className="py-24 text-center text-navy/50">Aucune annonce ne correspond à vos critères.</div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {pageItems.map((l, i) => (
                  <Reveal key={l.id} delay={(i % 3) * 70}>
                    <ListingCard l={l} />
                  </Reveal>
                ))}
              </div>
              <Pagination page={page} totalPages={totalPages} basePath="/annonces" />
            </>
          )}
        </div>
      </section>
    </>
  );
}
